'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface NavItem {
  id: string;
  label: string;
  href: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-overview', label: 'Herd Overview', href: '/dashboard' },
  { id: 'nav-cows', label: 'Cows', href: '/cow-management' },
  { id: 'nav-calves', label: 'Calves', href: '/calves' },
  { id: 'nav-bulls', label: 'Bulls', href: '/bull-turnout' },
  { id: 'nav-breeding', label: 'Breeding', href: '/breeding' },
  { id: 'nav-health', label: 'Health', href: '/health' },
  { id: 'nav-calving', label: 'Calving Watch', href: '/calving-watch' },
  { id: 'nav-notebook', label: 'Ranch Notebook', href: '/notebook' },
  { id: 'nav-reports', label: 'Reports', href: '/reports' },
  { id: 'nav-settings', label: 'Settings', href: '/settings' },
];

interface SidebarProps {
  currentPath?: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut, subscription } = useAuth();
  const router = useRouter();

  const isActive = (href: string) => {
    if (href === '/dashboard') return currentPath === '/dashboard';
    return currentPath?.startsWith(href) ?? false;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
    } catch {
      // ignore
    }
  };

  const trialDaysLeft = () => {
    if (!subscription || subscription.status !== 'trialing') return null;
    const end = new Date(subscription.trial_ends_at);
    const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const daysLeft = trialDaysLeft();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand Header */}
      <div className="px-5 py-5 border-b border-border">
        <p className="font-bold text-lg text-foreground leading-tight">CattleBooks</p>
        <p className="text-xs text-muted-foreground mt-0.5">Simple cattle records</p>
      </div>

      {/* Trial Banner */}
      {daysLeft !== null && (
        <div className="mx-3 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs font-semibold text-amber-800">
            {daysLeft} day{daysLeft !== 1 ? 's' : ''} left in trial
          </p>
          <Link href="/settings" className="text-xs text-amber-700 underline">
            Subscribe — $5/mo
          </Link>
        </div>
      )}

      {/* Nav Items */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.id}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className={`
                flex items-center px-3 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-150
                ${active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }
              `}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="px-3 py-4 border-t border-border space-y-1">
        {user && (
          <div className="px-3 py-2 rounded-lg">
            <p className="text-xs font-semibold text-foreground truncate">
              {user.user_metadata?.ranch_name || user.email?.split('@')[0] || 'Ranch'}
            </p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
        )}
        <button
          onClick={handleSignOut}
          className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
        >
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-52 bg-card border-r border-border shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-52 bg-card border-r border-border z-50
          transform transition-transform duration-300 ease-in-out lg:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b border-border flex items-center px-4 z-30">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors mr-3 text-sm font-medium"
          aria-label="Open menu"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <div>
          <span className="font-bold text-base text-foreground">CattleBooks</span>
        </div>
      </div>
    </>
  );
}